<template>
  <div class="cms-guide-form">
    <cms-form-header
      :title="isEditing ? $t('cms.guides.editGuide') : $t('cms.guides.createGuide')"
      :disabled="isSaving || isLoadingGuide"
      @back="goBack"
      @save-draft="saveDraft"
      @save-publish="saveAndPublish"
    />

    <cms-form-layout>
      <form @submit.prevent="handleSubmit">
        <cms-form-section :title="$t('cms.guides.userSelection')">
          <div class="cms-form-grid">
            <cms-select
              v-model="userSelectValue"
              :label="$t('cms.guides.user')"
              :options="userOptions"
              :placeholder="$t('cms.guides.userPlaceholder')"
              :full-width="false"
              :disabled="isEditing"
            />
          </div>
        </cms-form-section>

        <cms-form-section :title="$t('cms.guides.generalInfo')">
          <cms-translation-tabs :languages="languages" v-model="currentLanguage">
            <template #default="{ locale }">
              <div class="cms-form-grid">
                <cms-input
                  v-model="getTranslation(locale).title"
                  :label="`${$t('cms.guides.guideTitle')} (${locale.toUpperCase()})`"
                  :full-width="true"
                />
                <cms-input
                  :model-value="getTranslation(locale).slug"
                  :label="`${$t('cms.guides.slug')} (${locale.toUpperCase()})`"
                  :full-width="true"
                  @update:model-value="getTranslation(locale).slug = $event || ''"
                />
                <cms-input
                  :model-value="getTranslation(locale).tagline || ''"
                  :label="`${$t('cms.guides.tagline')} (${locale.toUpperCase()})`"
                  :full-width="true"
                  @update:model-value="getTranslation(locale).tagline = $event || ''"
                />
                <cms-editor
                  :model-value="getTranslation(locale).bio || ''"
                  :label="`${$t('cms.guides.bio')} (${locale.toUpperCase()})`"
                  @update:model-value="getTranslation(locale).bio = $event || ''"
                />
                <cms-editor
                  :model-value="getTranslation(locale).content || ''"
                  :label="`${$t('cms.guides.content')} (${locale.toUpperCase()})`"
                  @update:model-value="getTranslation(locale).content = $event || ''"
                />
                <cms-input
                  :model-value="getTranslation(locale).seo_title || ''"
                  :label="`${$t('cms.guides.seoTitle')} (${locale.toUpperCase()})`"
                  :full-width="true"
                  @update:model-value="getTranslation(locale).seo_title = $event || ''"
                />
                <cms-textarea
                  :model-value="getTranslation(locale).seo_description || ''"
                  :label="`${$t('cms.guides.seoDescription')} (${locale.toUpperCase()})`"
                  :rows="3"
                  :full-width="true"
                  :max-length="160"
                  :show-counter="true"
                  @update:model-value="getTranslation(locale).seo_description = $event || ''"
                />
                <cms-list-input
                  :model-value="getTranslation(locale).seo_keywords || []"
                  :label="`${$t('cms.guides.seoKeywords')} (${locale.toUpperCase()})`"
                  :placeholder="$t('cms.guides.seoKeywordsPlaceholder')"
                  :add-button-text="$t('cms.activities.addKeyword')"
                  :full-width="true"
                  @update:model-value="getTranslation(locale).seo_keywords = $event || []"
                />
              </div>
            </template>
          </cms-translation-tabs>
        </cms-form-section>

        <cms-form-section :title="$t('cms.guides.additionalInfo')">
          <div class="cms-form-grid">
            <div class="cms-form-group">
              <label>{{ $t('cms.guides.memberSince') }}</label>
              <Datepicker
                v-model="memberSinceDate"
                :enable-time-picker="false"
                :format="dateFormat"
                :input-class-name="'cms-input'"
                :month-change-on-scroll="false"
                auto-apply
                class="cms-datepicker"
              />
              <p class="cms-form-help">{{ $t('cms.guides.memberSinceHelp') }}</p>
            </div>
          </div>
        </cms-form-section>
      </form>

      <template #sidebar>
        <cms-sidebar>
          <cms-sidebar-section :title="$t('cms.guides.mediaSection')">
            <template v-if="guideId">
              <cms-image-upload
                ref="featuredImageUploadRef"
                v-model="form.featured_image"
                :label="$t('cms.guides.featuredImage')"
                :help-text="$t('cms.guides.featuredImageHelp')"
                :initial-media="featuredUploaderInitialMedia"
                @uploaded="handleFeaturedUploaded"
                @removed="handleFeaturedRemoved"
              />
              <div class="cms-media-actions" :class="{ 'is-media-saving': isSyncingFeaturedMedia }">
                <cms-button
                  variant="secondary"
                  size="sm"
                  :disabled="!canSaveFeaturedMedia || isSyncingFeaturedMedia"
                  @click="saveFeaturedMedia"
                >
                  <v-icon icon="upload" size="sm" />
                  {{ isSyncingFeaturedMedia ? $t('general.button.saving') : $t('cms.guides.uploadFeatured') }}
                </cms-button>
              </div>

              <div class="cms-sidebar-featured-alt">
                <cms-input
                  v-model="getTranslation(currentLanguage).featured_image_alt"
                  :label="`${$t('cms.guides.featuredImageAlt')} (${currentLanguage.toUpperCase()})`"
                  :help-text="$t('cms.guides.featuredImageAltHelp')"
                  :full-width="true"
                  :disabled="isSaving || isLoadingGuide"
                  :show-tooltip-on-hover="false"
                />
              </div>
            </template>
            <template v-else>
              <p class="cms-form-help cms-media-disabled">
                {{ $t('cms.activities.mediaUploadRequiresCreation') }}
              </p>
            </template>
          </cms-sidebar-section>

          <cms-sidebar-section :title="$t('cms.guides.publishing')">
            <label class="cms-checkbox-item">
              <input v-model="form.is_public" type="checkbox" class="cms-checkbox" style="margin-right: 8px;" />
              <span>{{ $t('cms.guides.isPublic') }}</span>
            </label>
            <label class="cms-checkbox-item">
              <input v-model="form.is_featured" type="checkbox" class="cms-checkbox" style="margin-right: 8px;" />
              <span>{{ $t('cms.guides.isFeatured') }}</span>
            </label>
            <label class="cms-checkbox-item">
              <input v-model="form.is_author" type="checkbox" class="cms-checkbox" style="margin-right: 8px;" />
              <span>{{ $t('cms.guides.isAuthor') }}</span>
            </label>
          </cms-sidebar-section>

          <cms-sidebar-section :title="$t('cms.guides.relationshipsSection')">
            <label class="cms-input-label">{{ $t('cms.guides.relatedActivities') }}</label>
            <v-select
              v-model="form.related_activities"
              :options="activityOptions"
              multiple
              label="label"
              :reduce="reduceActivityOption"
              class="cms-relationship-select"
              :close-on-select="false"
              :clearable="false"
            />
            <p class="cms-form-help">{{ $t('cms.guides.relatedActivitiesHelp') }}</p>
          </cms-sidebar-section>
        </cms-sidebar>
      </template>
    </cms-form-layout>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';
import { useAppLanguages } from '@/composables/useAppLanguages';
import { useAppStore } from '@/stores/app';
import type { GuiderForm, GuiderTranslation } from '@/types/cms';
import {
  CmsFormHeader,
  CmsFormLayout,
  CmsFormSection,
  CmsInput,
  CmsTextarea,
  CmsSelect,
  CmsButton,
  CmsEditor,
  CmsSidebar,
  CmsSidebarSection,
  CmsTranslationTabs,
  CmsImageUpload,
  CmsListInput,
} from '@/components/cms';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import { normalizeResource, extractRelationship } from '@/composables/useCmsActivityData';
import { hasPendingCmsMediaSave, persistCmsMediaAttachment } from '@/helpers/cmsMediaUpload';
import { runCmsMediaSync } from '@/helpers/cmsMediaSaveFlow';

interface SelectOption {
  value: number | string;
  label: string;
}

export default defineComponent({
  name: 'TheCmsGuideForm',
  components: {
    CmsFormHeader,
    CmsFormLayout,
    CmsFormSection,
    CmsInput,
    CmsTextarea,
    CmsSelect,
    CmsButton,
    CmsEditor,
    CmsSidebar,
    CmsSidebarSection,
    CmsTranslationTabs,
    CmsImageUpload,
    CmsListInput,
    vSelect,
    Datepicker,
    VIcon,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const toast = useToast();
    const authStore = useAuthStore();
    const appStore = useAppStore();

    const agencySlug = computed(() => authStore.agency?.slug ?? null);
    const dateFormat = computed(() => appStore.settings.formatDateHyphen);
    const guideId = ref(route.params.id ? Number(route.params.id) : null);
    const isEditing = computed(() => guideId.value !== null);

    const { languages, locales, resolveStoredLocale } = useAppLanguages();
    const currentLanguage = ref(resolveStoredLocale(localStorage.getItem('language')));

    const createEmptyTranslation = (locale: string): GuiderTranslation => ({
      locale,
      title: '',
      bio: '',
      tagline: '',
      content: '',
      slug: '',
      seo_title: '',
      seo_description: '',
      seo_keywords: [],
      featured_image_alt: '',
    });

    const form = reactive<GuiderForm>({
      agency_user_id: null,
      member_since: '',
      is_author: false,
      is_public: false,
      is_featured: false,
      related_activities: [],
      featured_image: '',
      translations: locales.value.map((locale) => createEmptyTranslation(locale)),
    });

    const activities = ref<SelectOption[]>([]);
    const activityOptions = computed(() => activities.value);

    const users = ref<SelectOption[]>([]);
    const userOptions = computed(() => users.value);
    const userSelectValue = computed<string | number>({
      get: () => {
        if (form.agency_user_id === null || typeof form.agency_user_id === 'undefined') {
          return '';
        }
        return form.agency_user_id;
      },
      set: (value: string | number) => {
        if (value === '' || value === null) {
          form.agency_user_id = null;
          return;
        }
        if (typeof value === 'number') {
          form.agency_user_id = value;
          return;
        }
        const parsed = Number(value);
        form.agency_user_id = Number.isNaN(parsed) ? null : parsed;
      },
    });

    const getTranslation = (locale: string): GuiderTranslation => {
      let translation = form.translations.find((entry) => entry.locale === locale);
      if (!translation) {
        translation = createEmptyTranslation(locale);
        form.translations.push(translation);
      }
      // Ensure all fields have default values if they're undefined
      if (translation.title === undefined) translation.title = '';
      if (translation.bio === undefined) translation.bio = '';
      if (translation.tagline === undefined) translation.tagline = '';
      if (translation.content === undefined) translation.content = '';
      if (translation.slug === undefined) translation.slug = '';
      if (translation.seo_title === undefined) translation.seo_title = '';
      if (translation.seo_description === undefined) translation.seo_description = '';
      if (translation.seo_keywords === undefined) translation.seo_keywords = [];
      if (translation.featured_image_alt === undefined) translation.featured_image_alt = '';
      return translation;
    };

    const stripHtmlContent = (value: string | null | undefined) => {
      if (!value) {
        return '';
      }
      return value.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
    };

    const requiredTranslationFields: Array<keyof GuiderTranslation> = ['title', 'bio'];

    const translationsAreComplete = computed(() => {
      return form.translations.some((translation) =>
        requiredTranslationFields.every((field) => {
          const value = translation[field];
          if (typeof value === 'string') {
            const normalized = field === 'bio' ? stripHtmlContent(value) : value.trim();
            return normalized.length > 0;
          }
          return false;
        }),
      );
    });

    const validateTranslations = () => {
      if (translationsAreComplete.value) {
        return true;
      }
      const missingFields = requiredTranslationFields
        .map((field) => t(`cms.guides.${field === 'title' ? 'guideTitle' : field}`))
        .join(', ');
      toast.error(
        t('cms.stories.translationValidationError', {
          fields: missingFields,
          language: t('cms.stories.translationGenericLanguage'),
        }),
      );
      return false;
    };

    const isSaving = ref(false);
    const isLoadingGuide = ref(false);

    const extractListData = (response: any) => {
      if (!response) return [];
      const candidates = [response?.data?.data, response?.data, response?.data?.data?.data, response];
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
          return candidate;
        }
      }
      return [];
    };

    const extractGuideData = (response: any) => {
      if (!response) return null;
      const candidates = [response?.data?.data, response?.data, response];
      for (const candidate of candidates) {
        if (!candidate) continue;
        if (candidate.attributes && typeof candidate.attributes === 'object') {
          return {
            id: candidate.id ?? candidate.attributes.id ?? null,
            ...candidate.attributes,
            relationships: candidate.relationships,
          };
        }
        if (candidate.id || candidate.translations) {
          return candidate;
        }
      }
      return null;
    };

    // Date handling for Datepicker
    const memberSinceDate = computed<Date | null>({
      get: () => {
        if (!form.member_since) return null;
        const date = new Date(form.member_since);
        return Number.isNaN(date.getTime()) ? null : date;
      },
      set: (value: Date | null) => {
        if (value && value instanceof Date && !Number.isNaN(value.getTime())) {
          form.member_since = value.toISOString().split('T')[0];
        } else {
          form.member_since = '';
        }
      },
    });

    const mapGuideToForm = (guide: any) => {
      // Extract agency_user_id - check direct property, attributes, or relationship
      const agencyUserRelation = extractRelationship(guide, 'agencyUser');
      const normalizedAgencyUser = normalizeResource(agencyUserRelation);
      const agencyUserId = 
        guide.agency_user_id ?? 
        guide.agencyUserId ?? 
        (normalizedAgencyUser?.id ? Number(normalizedAgencyUser.id) : null) ??
        null;
      form.agency_user_id = agencyUserId !== undefined && agencyUserId !== null ? Number(agencyUserId) : null;
      form.member_since = guide.member_since || '';
      form.is_author = guide.is_author || false;
      form.is_public = guide.is_public || false;
      form.is_featured = guide.is_featured || false;
      mapGuideMediaCollections(guide);

      const relatedActivities = extractRelationship(guide, 'relatedActivities');
      const normalized = normalizeResource(relatedActivities);
      if (Array.isArray(normalized)) {
        form.related_activities = normalized
          .map((activity: any) => activity?.id)
          .filter((id: any): id is number => typeof id === 'number');
      } else if (Array.isArray(guide.relatedActivities)) {
        form.related_activities = guide.relatedActivities
          .map((activity: any) => activity?.id)
          .filter((id: any): id is number => typeof id === 'number');
      } else {
        form.related_activities = [];
      }
    };

    const applyTranslationsFromGuide = (guide: any, locale: string) => {
      const translations = extractRelationship(guide, 'translations');
      const normalized = normalizeResource(translations);
      const translation = Array.isArray(normalized) ? normalized[0] : normalized || guide.translations?.[0];
      if (!translation) {
        return;
      }
      const target = getTranslation(locale);
      target.title = translation.title || '';
      target.bio = translation.bio || '';
      target.tagline = translation.tagline || '';
      target.slug = translation.slug || '';
      target.content = translation.content || '';
      target.seo_title = translation.seo_title || '';
      target.seo_description = translation.seo_description || '';
      target.seo_keywords = translation.seo_keywords || [];
      target.featured_image_alt = translation.featured_image_alt ?? translation.featuredImageAlt ?? '';
    };

    const buildPayload = () => {
      const translationsPayload = form.translations
        .map((translation) => ({
          locale: translation.locale,
          title: translation.title?.trim(),
          bio: translation.bio,
          tagline: translation.tagline?.trim(),
          slug: translation.slug?.trim(),
          content: translation.content,
          seo_title: translation.seo_title?.trim(),
          seo_description: translation.seo_description?.trim(),
          seo_keywords: translation.seo_keywords,
          featured_image_alt: translation.featured_image_alt?.trim() || '',
        }))
        .filter((translation) =>
          requiredTranslationFields.every((field) => {
            const value = translation[field];
            if (typeof value === 'string') {
              const normalized = field === 'bio' ? stripHtmlContent(value) : value.trim();
              return normalized.length > 0;
            }
            return false;
          }),
        );

      const payload: any = {
        member_since: form.member_since || null,
        is_author: form.is_author || false,
        is_public: form.is_public || false,
        is_featured: form.is_featured || false,
        related_activities: form.related_activities,
        translations: translationsPayload,
      };

      // Always include agency_user_id, even if null
      if (form.agency_user_id !== null && form.agency_user_id !== undefined) {
        payload.agency_user_id = form.agency_user_id;
      } else {
        payload.agency_user_id = null;
      }

      return payload;
    };

    const extractGuideId = (response: any): number | null => {
      if (!response) return null;
      if (typeof response.id === 'number') {
        return response.id;
      }
      if (typeof response?.data?.id === 'number') {
        return response.data.id;
      }
      if (typeof response?.data?.data?.id === 'number') {
        return response.data.data.id;
      }
      return null;
    };

    const loadActivities = async () => {
      if (!agencySlug.value) return;
      try {
        const response = await api.activityService.listCmsActivities(agencySlug.value, {
          perPage: 100,
          order: { key: 'updated_at', direction: 'desc' },
        });
        const list = extractListData(response);
        activities.value = list.map((item: any) => {
          const normalized = normalizeResource(item) || item;
          const translations = extractRelationship(normalized, 'translations');
          const normalizedTranslations = normalizeResource(translations);
          const translation = Array.isArray(normalizedTranslations) 
            ? normalizedTranslations[0] 
            : normalizedTranslations;
          return {
            value: normalized.id,
            label: translation?.title || normalized.title || normalized.name || `#${normalized.id}`,
          };
        });
      } catch (error) {
        console.error('Failed to load activities', error);
      }
    };

    const loadUsers = async () => {
      if (!agencySlug.value) return;
      try {
        const response: any = await api.agencyUser.all(agencySlug.value);
        const list = Array.isArray(response) ? response : (response?.data ? response.data : []);
        users.value = list.map((item: any) => ({
          value: item.id,
          label: item?.user?.name || item?.name || `#${item.id}`,
        }));
      } catch (error) {
        console.error('Failed to load users', error);
      }
    };

    // Featured Image Management
    const featuredImageUploadRef = ref<any>(null);
    const featuredUploaderInitialMedia = ref<any[]>([]);
    const featuredMediaPayload = ref<any[] | null>(null);
    const pendingFeaturedDeletion = ref<any | null>(null);
    const isSyncingFeaturedMedia = ref(false);

    const CMS_MEDIA_MODEL_TYPE = 'cms_guider';
    const FEATURED_MEDIA_COLLECTION = 'cms_guider_featured';
    const MEDIA_DRIVER = 's3';

    const cloneMediaItems = (items: any[] = []) => items.map((item) => JSON.parse(JSON.stringify(item)));

    const getCollectionName = (item: any) => item?.collection_name || item?.collectionName || '';
    const getCustomProperties = (item: any) => item?.custom_properties || item?.customProperties || {};
    const getOrderColumn = (item: any) => item?.order_column ?? item?.orderColumn;

    const getMediaUrl = (item: any) => {
      if (!item) return '';
      const attributes = item.attributes || {};
      return (
        item.storage_url ||
        item.storageUrl ||
        attributes.storage_url ||
        attributes.storageUrl ||
        item.original_url ||
        item.originalUrl ||
        attributes.original_url ||
        attributes.originalUrl ||
        item.preview_url ||
        attributes.preview_url ||
        ''
      );
    };

    const getMediaPreviewUrl = (item: any, fallback: string) => {
      const attributes = item?.attributes || {};
      return (
        item.preview_url ||
        item.previewUrl ||
        attributes.preview_url ||
        attributes.previewUrl ||
        fallback
      );
    };

    const normalizeMediaForUploader = (item: any, fallbackOrder = 1) => {
      if (!item) return null;
      const customProps = getCustomProperties(item);
      const order = customProps?.order ?? getOrderColumn(item) ?? fallbackOrder;
      const storageUrl = getMediaUrl(item);
      const previewUrl = getMediaPreviewUrl(item, storageUrl);

      return {
        ...item,
        order,
        order_column: order,
        original_url: storageUrl,
        storage_url: storageUrl,
        preview_url: previewUrl,
        attributes: {
          ...(item.attributes || {}),
          uuid: item.uuid,
          name: item.name || `media-${order}`,
          order_column: order,
          collection_name: getCollectionName(item),
          original_url: storageUrl,
          preview_url: previewUrl,
          isExisting: true,
          urls: {
            ...(item.attributes?.urls || {}),
            original: storageUrl,
            preview: previewUrl,
          },
        },
      };
    };

    const extractMediaItems = (guide: any) => {
      const mediaRelationship = extractRelationship(guide, 'media');
      const normalizedRelationship = normalizeResource(mediaRelationship);
      if (Array.isArray(normalizedRelationship)) {
        return normalizedRelationship;
      }
      if (Array.isArray(guide.media)) {
        return guide.media;
      }
      return [];
    };

    const mapGuideMediaCollections = (guide: any) => {
      const mediaItems = extractMediaItems(guide)
        .map((item: any) => normalizeResource(item) || item)
        .filter((item: any) => !!item && (item.uuid || item.id));

      const featuredItems = mediaItems
        .filter((item: any) => getCollectionName(item) === FEATURED_MEDIA_COLLECTION)
        .map((item: any, index: number) => normalizeMediaForUploader(item, index + 1))
        .filter(Boolean);

      featuredUploaderInitialMedia.value = cloneMediaItems(featuredItems);
      pendingFeaturedDeletion.value = null;

      const featuredUrl = getMediaUrl(featuredItems[0]);
      form.featured_image = featuredUrl || '';
    };

    const deleteMediaItemsByIds = async (ids: Array<number | null | undefined>) => {
      const uniqueIds = Array.from(
        new Set((ids || []).filter((id): id is number => typeof id === 'number' && id > 0)),
      );
      if (!uniqueIds.length) {
        return;
      }
      await Promise.all(uniqueIds.map((id) => api.media.delete(MEDIA_DRIVER, id)));
    };

    const buildMediaConfirmEntries = (mediaItems: any[] = [], collection: string) => {
      return (Array.isArray(mediaItems) ? mediaItems : [])
        .map((item: any, index: number) => {
          const attributes = item?.attributes ?? {};
          if (attributes.isExisting) {
            return null;
          }
          const uuid = attributes.uuid ?? item.uuid;
          if (!uuid) {
            return null;
          }
          const order = attributes.order ?? attributes.order_column ?? item.order ?? index + 1;
          return {
            uuid,
            name: attributes.name ?? item.name ?? `media-${order}`,
            order,
            collection,
          };
        })
        .filter((entry): entry is { uuid: string; name: string; order: number; collection: string } => !!entry);
    };

    const refreshGuideMediaState = async () => {
      if (!agencySlug.value || !guideId.value) {
        return;
      }
      try {
        const response = await api.guiderService.getCmsGuider(agencySlug.value, guideId.value);
        const guideData = extractGuideData(response);
        if (guideData) {
          mapGuideMediaCollections(guideData);
        }
      } catch (error) {
        console.error('Error refreshing guide media', error);
      }
    };

    const confirmFeaturedMedia = async () => {
      if (!guideId.value || isSyncingFeaturedMedia.value || isLoadingGuide.value) {
        return;
      }
      const payload = featuredMediaPayload.value ?? [];
      const hasUploads = hasPendingCmsMediaSave(payload, FEATURED_MEDIA_COLLECTION);
      const hasPendingDeletion = !!pendingFeaturedDeletion.value?.id;
      if (!hasUploads && !hasPendingDeletion) {
        return;
      }
      try {
        await runCmsMediaSync(isSyncingFeaturedMedia, async () => {
          if (hasPendingDeletion) {
            await deleteMediaItemsByIds([pendingFeaturedDeletion.value.id]);
            pendingFeaturedDeletion.value = null;
          } else if (hasUploads && featuredUploaderInitialMedia.value.length > 0) {
            await deleteMediaItemsByIds(featuredUploaderInitialMedia.value.map((item: any) => item.id));
          }
          if (hasUploads) {
            await persistCmsMediaAttachment({
              api,
              driver: MEDIA_DRIVER,
              agencySlug: agencySlug.value!,
              mediaItems: payload,
              modelType: CMS_MEDIA_MODEL_TYPE,
              modelId: guideId.value,
              collection: FEATURED_MEDIA_COLLECTION,
            });
          }
          featuredMediaPayload.value = null;
          featuredImageUploadRef.value?.clearMedia?.();
          await refreshGuideMediaState();
        });
        toast.success(t('cms.activities.featuredMediaSaveSuccess'));
      } catch (error) {
        console.error('Error saving featured media', error);
        toast.error(t('general.errorMessage'));
      }
    };

    const persistPendingMediaChanges = async () => {
      await confirmFeaturedMedia();
    };

    const handleFeaturedUploaded = (media: any[] = []) => {
      featuredMediaPayload.value = Array.isArray(media) ? [...media] : [];
    };

    const handleFeaturedRemoved = (media: any[] = []) => {
      featuredMediaPayload.value = Array.isArray(media) ? [...media] : [];
      if ((!media || media.length === 0) && featuredUploaderInitialMedia.value.length > 0) {
        pendingFeaturedDeletion.value = featuredUploaderInitialMedia.value[0];
      }
    };

    const canSaveFeaturedMedia = computed(
      () =>
        !!guideId.value &&
        featuredMediaPayload.value !== null &&
        !isSyncingFeaturedMedia.value &&
        !isSaving.value &&
        !isLoadingGuide.value,
    );

    const saveFeaturedMedia = async () => {
      await confirmFeaturedMedia();
    };

    const loadGuide = async () => {
      if (!agencySlug.value || !guideId.value) {
        return;
      }
      isLoadingGuide.value = true;
      try {
        const response = await api.guiderService.getCmsGuider(agencySlug.value, guideId.value, {
          headers: {
            'Accept-Language': currentLanguage.value,
          },
        });
        const guideData = extractGuideData(response);
        if (guideData) {
          mapGuideToForm(guideData);
          for (const locale of locales.value) {
            applyTranslationsFromGuide(guideData, locale);
          }
        }
      } catch (error) {
        console.error('Failed to load guide', error);
        toast.error(t('general.errorMessage'));
      } finally {
        isLoadingGuide.value = false;
      }
    };

    const persistGuide = async (publish = false) => {
      if (!agencySlug.value) return;
      if (!validateTranslations()) {
        return;
      }
      const payload = buildPayload();
      isSaving.value = true;
      try {
        let response;
        if (isEditing.value && guideId.value) {
          response = await api.guiderService.updateCmsGuider(agencySlug.value, guideId.value, payload);
        } else {
          response = await api.guiderService.createCmsGuider(agencySlug.value, payload);
          const newId = extractGuideId(response);
          if (newId) {
            guideId.value = newId;
            await router.replace({ name: 'cms.guiders.edit', params: { id: newId } });
          }
        }

        if (publish) {
          await api.guiderService.publishCmsGuider(agencySlug.value, guideId.value!, { is_public: true });
        }

        await persistPendingMediaChanges();
       
        toast.success(publish ? t('cms.guides.publishSuccess') : t('general.shared.savedChanges'));
      } catch (error: any) {
        console.error('Error saving guide', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        isSaving.value = false;
      }
    };

    const reduceActivityOption = (option: SelectOption) => option.value;

    const saveDraft = () => {
      persistGuide(false);
    };

    const saveAndPublish = () => {
      persistGuide(true);
    };

    const handleSubmit = () => {
      saveDraft();
    };

    const goBack = () => {
      router.push({ name: 'cms.guiders' });
    };

    watch(
      () => route.params.id,
      (newId) => {
        guideId.value = newId ? Number(newId) : null;
        if (guideId.value) {
          loadGuide();
        } else {
          form.agency_user_id = null;
          form.member_since = '';
          form.is_author = false;
          form.is_public = false;
          form.is_featured = false;
          form.related_activities = [];
          form.featured_image = '';
          form.translations = locales.value.map((locale) => createEmptyTranslation(locale));
          featuredUploaderInitialMedia.value = [];
          featuredMediaPayload.value = null;
          pendingFeaturedDeletion.value = null;
        }
      },
      { immediate: true },
    );

    watch(
      () => agencySlug.value,
      (slug) => {
        if (slug) {
          loadUsers();
          loadActivities();
          if (guideId.value) {
            loadGuide();
          }
        }
      },
      { immediate: true },
    );

    return {
      form,
      isEditing,
      isSaving,
      isLoadingGuide,
      languages,
      currentLanguage,
      getTranslation,
      userSelectValue,
      userOptions,
      activityOptions,
      reduceActivityOption,
      featuredImageUploadRef,
      featuredUploaderInitialMedia,
      canSaveFeaturedMedia,
      saveFeaturedMedia,
      handleFeaturedUploaded,
      handleFeaturedRemoved,
      memberSinceDate,
      dateFormat,
      saveDraft,
      saveAndPublish,
      handleSubmit,
      goBack,
      guideId,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/cms-styles.scss';

// ============================================
// VIEW-SPECIFIC STYLES
// ============================================
// Only styles unique to this view

.cms-guide-form {
  color: $cms-text-primary;
  
  // User selection section styling
  .cms-form-section:first-of-type {
    background: $cms-background-light;
    padding: $cms-spacing-lg;
    border-radius: $cms-radius-md;
    border: 1px solid $cms-border-color-light;
    margin-bottom: $cms-spacing-xl;
    
    .cms-form-grid {
      max-width: 450px;
    }
  }
  
  // General form sections
  :deep(.cms-form-section) {
    margin-bottom: $cms-spacing-xl;
    
    &:not(:first-of-type) {
      padding: $cms-spacing-lg;
      background: $cms-background-white;
      border-radius: $cms-radius-md;
      border: 1px solid $cms-border-color-light;
    }
  }
}

// Guide actions (unique to this view)
.cms-guide-actions {
  display: flex;
  justify-content: flex-end;
  gap: $cms-spacing-sm;
  margin-top: $cms-spacing-lg;
}

// Media actions (unique to this view)
.cms-media-actions {
  margin-top: $cms-spacing-xs;
  display: flex;
  justify-content: flex-end;
}

.cms-media-disabled {
  color: $cms-text-secondary;
}

// Relationship select (unique to this view)
.cms-relationship-select {
  width: 100%;
}

// Datepicker (unique to this view)
.cms-datepicker {
  width: 100%;
  
  :deep(.dp__input_wrap) {
    width: 100%;
  }
  
  :deep(.dp__input) {
    width: 100%;
    padding: $cms-spacing-sm $cms-spacing-md;
    border: 1px solid $cms-border-color-light;
    border-radius: $cms-radius-md;
    font-size: $cms-font-size-base;
    color: $cms-text-primary;
    background-color: $cms-background-white;
    transition: all $cms-transition-base;
    
    &:hover {
      border-color: rgba($cms-primary-color, 0.3);
    }
    
    &:focus {
      outline: none;
      border-color: $cms-primary-color;
      box-shadow: 0 0 0 3px rgba($cms-primary-color, 0.1);
    }
  }
}
</style>

